import { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { ClipboardList, Plus, Trash2, FileDown, Camera, Mail, Search } from 'lucide-react';
import { toast } from 'sonner';
import { formatDatePT } from '../utils/dateFormat';
import { DamageReportEditor } from './damage/DamageReportEditor';
import { downloadReportPdf } from './damage/downloadReportPdf';

const STATUS = {
  concept: 'bg-amber-100 text-amber-800',
  definitief: 'bg-blue-100 text-blue-800',
  verzonden: 'bg-green-100 text-green-800',
};

const DamageReports = ({ user }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState('');

  const load = async () => {
    try {
      const res = await axiosInstance.get('/schade-rapporten');
      setReports(res.data || []);
    } catch (e) {
      toast.error('Schaderapporten laden mislukt');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const createNew = async () => {
    try {
      const res = await axiosInstance.post('/schade-rapporten', { report_date: new Date().toISOString().slice(0, 10) });
      setReports([res.data, ...reports]);
      setActive(res.data);
    } catch (e) {
      toast.error('Aanmaken mislukt');
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Schaderapport ${r.number} verwijderen?`)) return;
    try {
      await axiosInstance.delete(`/schade-rapporten/${r.id}`);
      setReports(reports.filter(x => x.id !== r.id));
      toast.success('Verwijderd');
    } catch (e) {
      toast.error('Verwijderen mislukt');
    }
  };

  if (active) {
    return (
      <DamageReportEditor
        report={active}
        user={user}
        onBack={() => { setActive(null); load(); }}
        onChange={(r) => { setActive(r); setReports(reports.map(x => x.id === r.id ? r : x)); }}
      />
    );
  }

  const term = q.trim().toLowerCase();
  const list = term
    ? reports.filter(r => [r.number, r.client_name, r.client_address, r.client_city, r.title, r.invoice_number].some(v => (v || '').toLowerCase().includes(term)))
    : reports;

  return (
    <div className="space-y-6" data-testid="damage-reports-page">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-3"><ClipboardList className="w-9 h-9 text-[#3da5a5]" />Schaderapporten</h1>
          <p className="text-gray-500 text-sm mt-1">Officieel schaderapport met bedrijfsgegevens, foto's en handtekening — als PDF of per e-mail.</p>
        </div>
        <Button onClick={createNew} data-testid="new-damage-report-btn" className="bg-[#3da5a5] hover:bg-[#2d8585] text-white font-semibold px-6 py-6 rounded-xl">
          <Plus className="w-5 h-5 mr-2" />Nieuw schaderapport
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Zoek op nummer, klant of adres…" className="pl-9 bg-white" data-testid="damage-reports-search" />
      </div>

      {loading ? (
        <p className="text-gray-500">Laden…</p>
      ) : list.length === 0 ? (
        <Card className="bg-gray-50 border-gray-200"><CardContent className="pt-6 text-center text-gray-600" data-testid="damage-reports-empty">Nog geen schaderapporten. Klik op "Nieuw schaderapport" om te beginnen.</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {list.map(r => (
            <Card key={r.id} className="bg-white border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-testid={`damage-report-row-${r.number}`}>
              <CardContent className="py-4 flex flex-wrap items-center gap-4">
                <button onClick={() => setActive(r)} className="flex-1 min-w-[240px] text-left" data-testid={`open-damage-report-${r.number}`}>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-800">{r.number}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS[r.status] || STATUS.concept}`}>{r.status}</span>
                    <span className="text-xs text-gray-400">{formatDatePT(r.report_date)}</span>
                  </div>
                  <p className="text-gray-800 mt-1">{r.client_name || <span className="text-gray-400 italic">Geen klant</span>}{r.client_address ? ` · ${r.client_address}${r.client_city ? ', ' + r.client_city : ''}` : ''}</p>
                  {r.title && <p className="text-gray-500 text-sm">{r.title}</p>}
                </button>
                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <span className="flex items-center gap-1"><Camera className="w-4 h-4" />{(r.photos || []).length}</span>
                  {r.sent_at && <span className="flex items-center gap-1 text-green-700"><Mail className="w-4 h-4" />verzonden</span>}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => downloadReportPdf(r)} data-testid={`pdf-damage-report-${r.number}`}><FileDown className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(r)} className="bg-red-600 hover:bg-red-700 text-white border-none" data-testid={`delete-damage-report-${r.number}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DamageReports;
